import { useEffect, useState } from 'react';
import type { GoonghapScores } from '@/utils/goonghapCalculations';

interface RadarChartProps {
  scores: GoonghapScores;
  animate?: boolean;
  isEn?: boolean;
}

const LABELS = ['성향', '타이밍', '재물', '소통', '애정'];
const LABELS_EN = ['Personality', 'Timing', 'Wealth', 'Comm', 'Affection'];
const ICONS  = ['🔥', '⏳', '💰', '💬', '💖'];
const CX = 120, CY = 120, R = 78;

function getPoint(angleIndex: number, ratio: number): [number, number] {
  const angle = (Math.PI * 2 * angleIndex / 5) - Math.PI / 2;
  return [CX + R * ratio * Math.cos(angle), CY + R * ratio * Math.sin(angle)];
}

function getLabelPoint(angleIndex: number): [number, number] {
  const angle = (Math.PI * 2 * angleIndex / 5) - Math.PI / 2;
  return [CX + (R + 32) * Math.cos(angle), CY + (R + 32) * Math.sin(angle)];
}

function toPolygon(ratios: number[]): string {
  return ratios.map((r, i) => getPoint(i, r).join(',')).join(' ');
}

export default function RadarChart({ scores, animate = true, isEn = false }: RadarChartProps) {
  const rawRatios = [
    scores.personality / 100,
    scores.timing / 100,
    scores.wealth / 100,
    scores.communication / 100,
    scores.affection / 100,
  ];

  const [ratios, setRatios] = useState(rawRatios.map(() => 0));
  const labels = isEn ? LABELS_EN : LABELS;

  useEffect(() => {
    if (!animate) { setRatios(rawRatios); return; }
    let progress = 0;
    const interval = setInterval(() => {
      progress += 0.04;
      if (progress >= 1) { setRatios(rawRatios); clearInterval(interval); return; }
      setRatios(rawRatios.map(r => r * progress));
    }, 20);
    return () => clearInterval(interval);
  }, []);

  const gridLevels = [0.25, 0.5, 0.75, 1.0];

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 240 240" className="w-full max-w-[260px] mx-auto overflow-visible">
        {/* 배경 그리드 */}
        {gridLevels.map(level => (
          <polygon
            key={level}
            points={toPolygon([level, level, level, level, level])}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="1"
          />
        ))}

        {/* 축 선 */}
        {LABELS.map((_, i) => {
          const [x, y] = getPoint(i, 1);
          return <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="#E5E7EB" strokeWidth="1" />;
        })}

        {/* 데이터 폴리곤 */}
        <polygon
          points={toPolygon(ratios)}
          fill="rgba(17,24,39,0.12)"
          stroke="#111827"
          strokeWidth="2"
          strokeLinejoin="round"
        />

        {/* 데이터 포인트 */}
        {ratios.map((r, i) => {
          const [x, y] = getPoint(i, r);
          return <circle key={i} cx={x} cy={y} r="4" fill="#111827" />;
        })}

        {/* 라벨 + 점수 (통합) */}
        {labels.map((label, i) => {
          const [lx, ly] = getLabelPoint(i);
          const score = Math.round(rawRatios[i] * 100);
          return (
            <text
              key={i}
              textAnchor="middle"
              fontFamily="'Noto Sans KR', sans-serif"
            >
              <tspan
                x={lx}
                y={ly - 5}
                fontSize="10"
                fill="#6B7280"
                fontWeight="500"
              >
                {ICONS[i]} {label}
              </tspan>
              <tspan
                x={lx}
                y={ly + 9}
                fontSize="10"
                fill="#111827"
                fontWeight="800"
              >
                {score}
              </tspan>
            </text>
          );
        })}
      </svg>
    </div>
  );
}
